import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  LineChart as LineChartIcon, 
  Calendar, 
  Download, 
  IndianRupee, 
  TrendingUp, 
  Receipt, 
  Users, 
  Package, 
  AlertCircle, 
  Star, 
  Loader2, 
  CheckCircle2,
  Filter,
  Layers,
  ArrowUpRight,
  Wallet,
  RefreshCw
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

export function ServicesReportsView({ branchId: propBranchId }: { branchId?: string | null }) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();

  const [resolvedBranchId, setResolvedBranchId] = useState<string | null>(propBranchId || activeBranchId || null);
  const [branches, setBranches] = useState<Array<{ id: string; branch_name: string }>>([]);

  // Presets: 'today' | '7days' | 'month' | 'custom'
  const [preset, setPreset] = useState<'today' | '7days' | 'month' | 'custom'>('month');

  // Dates in YYYY-MM-DD
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // 1st of current month
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Loading states
  const [loading, setLoading] = useState(true);

  // Data states
  const [summary, setSummary] = useState<any>(null);
  const [revenueByService, setRevenueByService] = useState<any[]>([]);
  const [performance, setPerformance] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [dailyTrend, setDailyTrend] = useState<any[]>([]);
  const [outstanding, setOutstanding] = useState<any[]>([]);
  const [onlineCollectionsData, setOnlineCollectionsData] = useState<{
    totals?: {
      count?: number;
      gross?: number;
      fee?: number;
      commission?: number;
      merchant_share?: number;
      pending_transfer?: number;
    };
    rows?: any[];
  } | null>(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  // Fetch branches
  useEffect(() => {
    if (!supabase || !currentTenantId) return;
    supabase
      .from('branches')
      .select('id, branch_name')
      .eq('tenant_id', currentTenantId)
      .then((res: any) => {
        if (res.data) setBranches(res.data);
      });
  }, [supabase, currentTenantId]);

  // Task 4: Fetch online collections via get_service_online_collections RPC
  const fetchOnlineCollections = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setCollectionsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_service_online_collections', {
        p_tenant_id: currentTenantId,
        p_from: fromDate || null,
        p_to: toDate || null
      });
      if (error) {
        console.warn('get_service_online_collections error:', error);
      } else if (data) {
        setOnlineCollectionsData(data);
      }
    } catch (err: any) {
      console.error('get_service_online_collections exception:', err);
    } finally {
      setCollectionsLoading(false);
    }
  }, [supabase, currentTenantId, fromDate, toDate]);

  useEffect(() => {
    fetchOnlineCollections();
  }, [fetchOnlineCollections]);

  // Preset button click handler
  const handlePresetChange = (selected: 'today' | '7days' | 'month') => {
    setPreset(selected);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    if (selected === 'today') {
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (selected === '7days') {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      setFromDate(d.toISOString().split('T')[0]);
      setToDate(todayStr);
    } else if (selected === 'month') {
      const d = new Date();
      d.setDate(1);
      setFromDate(d.toISOString().split('T')[0]);
      setToDate(todayStr);
    }
  };

  // Main Report Loading
  const loadReports = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      const branchParam = resolvedBranchId || null;

      // 1. Summary KPI
      const { data: sumData, error: sumErr } = await supabase.rpc('get_services_report_summary', {
        p_tenant_id: currentTenantId,
        p_from: fromDate,
        p_to: toDate,
        p_branch_id: branchParam
      });
      if (sumErr) throw sumErr;
      setSummary(sumData);

      // 2. Revenue by Service
      const { data: revData, error: revErr } = await supabase.rpc('get_services_revenue_by_service', {
        p_tenant_id: currentTenantId,
        p_from: fromDate,
        p_to: toDate,
        p_branch_id: branchParam
      });
      if (revErr) throw revErr;
      setRevenueByService(revData || []);

      // 3. Professional Performance
      const { data: perfData, error: perfErr } = await supabase.rpc('get_services_professional_performance', {
        p_tenant_id: currentTenantId,
        p_from: fromDate,
        p_to: toDate,
        p_branch_id: branchParam
      });
      if (perfErr) throw perfErr;
      setPerformance(perfData || []);

      // 4. Materials Consumption
      const { data: matData, error: matErr } = await supabase.rpc('get_services_materials_consumption', {
        p_tenant_id: currentTenantId,
        p_from: fromDate,
        p_to: toDate,
        p_branch_id: branchParam
      });
      if (matErr) throw matErr;
      setMaterials(matData || []);

      // 5. Daily Trend
      const { data: trendData, error: trendErr } = await supabase.rpc('get_services_daily_trend', {
        p_tenant_id: currentTenantId,
        p_from: fromDate,
        p_to: toDate,
        p_branch_id: branchParam
      });
      if (trendErr) throw trendErr;
      setDailyTrend(trendData || []);

      // 6. Outstanding Receivables (date agnostic)
      const { data: outData, error: outErr } = await supabase.rpc('get_services_outstanding', {
        p_tenant_id: currentTenantId,
        p_branch_id: branchParam
      });
      if (outErr) throw outErr;
      setOutstanding(outData || []);
    } catch (err: any) {
      console.error('Error loading service reports:', err);
      // Surface exact backend error text
      toast.error(err.message || 'Failed to load services reports');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, fromDate, toDate, resolvedBranchId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // CSV Export Helper
  const exportToCSV = (rows: any[], filename: string) => {
    if (!rows || rows.length === 0) {
      toast.info('No data available to export');
      return;
    }

    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        headers.map(h => {
          let val = row[h];
          if (val === null || val === undefined) return '""';
          if (typeof val === 'string') return `"${val.replace(/"/g, '""')}"`;
          return `"${val}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filename}.csv`);
  };

  return (
    <div className="flex-1 p-4 sm:p-6 h-full overflow-auto bg-slate-50 dark:bg-slate-900 space-y-6">
      {/* Header & Filter Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <LineChartIcon className="text-primary" size={22} />
            <span>Daily Services — Reports & Analytics</span>
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Revenue breakdown, professional commission performance, material consumption, and daily trends.
          </p>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Branch Filter */}
          <div className="flex items-center gap-1.5 text-xs">
            <Filter size={13} className="text-slate-400" />
            <select
              value={resolvedBranchId || ''}
              onChange={e => setResolvedBranchId(e.target.value || null)}
              className="h-9 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-700 dark:text-slate-200 outline-none"
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.branch_name}</option>
              ))}
            </select>
          </div>

          {/* Preset Buttons */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl text-xs">
            <button
              onClick={() => handlePresetChange('today')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                preset === 'today' ? 'bg-white dark:bg-slate-900 text-primary font-bold shadow-xs' : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => handlePresetChange('7days')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                preset === '7days' ? 'bg-white dark:bg-slate-900 text-primary font-bold shadow-xs' : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => handlePresetChange('month')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                preset === 'month' ? 'bg-white dark:bg-slate-900 text-primary font-bold shadow-xs' : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              This Month
            </button>
          </div>

          {/* Date Picker Range */}
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={fromDate}
              onChange={e => {
                setFromDate(e.target.value);
                setPreset('custom');
              }}
              className="h-9 text-xs w-36 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl"
            />
            <span className="text-xs text-slate-400">to</span>
            <Input
              type="date"
              value={toDate}
              onChange={e => {
                setToDate(e.target.value);
                setPreset('custom');
              }}
              className="h-9 text-xs w-36 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl"
            />
          </div>

          <Button
            onClick={loadReports}
            size="sm"
            disabled={loading}
            className="h-9 px-4 text-xs bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : 'Apply'}
          </Button>
        </div>
      </div>

      {loading && !summary ? (
        <div className="py-20 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
          <Loader2 size={28} className="animate-spin text-primary" />
          <span>Crunching Daily Services figures from database...</span>
        </div>
      ) : (
        <>
          {/* ======================================================== */}
          {/* 1. KPI SUMMARY CARDS                                     */}
          {/* ======================================================== */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3.5">
            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Total Bookings
                </span>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {summary?.bookings_total ?? 0}
                </p>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  Completed: {summary?.bookings_by_status?.completed ?? 0}
                </span>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Invoiced Revenue
                </span>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  ₹{Number(summary?.invoice_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  {summary?.invoices_count ?? 0} Invoices Generated
                </span>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Total Collected
                </span>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  ₹{Number(summary?.collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  Cash & Online Pay
                </span>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Outstanding Due
                </span>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                  ₹{Number(summary?.outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  Pending customer receipts
                </span>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  GST Tax Collected
                </span>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                  ₹{Number(summary?.tax_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </p>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  CGST + SGST + IGST
                </span>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardContent className="p-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  Average Rating
                </span>
                <div className="flex items-center gap-1.5 mt-1">
                  <Star size={18} className="fill-amber-400 text-amber-400" />
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">
                    {summary?.avg_rating ? Number(summary.avg_rating).toFixed(1) : '—'}
                  </span>
                </div>
                <span className="text-[11px] text-slate-500 mt-0.5 block">
                  {summary?.ratings_count ?? 0} Customer Reviews
                </span>
              </CardContent>
            </Card>
          </div>

          {/* Unbilled Warning if any */}
          {Number(summary?.unbilled_completed_jobs || 0) > 0 && (
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-400 shrink-0" />
                <span>
                  <strong>{summary.unbilled_completed_jobs} completed job(s)</strong> have not generated a GST invoice yet (Value: ₹{Number(summary.unbilled_completed_amount).toFixed(2)}).
                </span>
              </div>
              <span className="text-[11px] text-amber-400 font-semibold">
                Generate invoices in Smart Billing to finalize accounting
              </span>
            </div>
          )}

          {/* ======================================================== */}
          {/* 2. DAILY TREND LINE CHART (Recharts)                     */}
          {/* ======================================================== */}
          <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
            <CardHeader className="p-5 pb-2 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <TrendingUp size={16} className="text-primary" />
                  <span>Daily Bookings & Invoiced Revenue Trend</span>
                </CardTitle>
                <CardDescription className="text-xs text-slate-400">
                  Track volume of daily bookings alongside billed value over time.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportToCSV(dailyTrend, 'services_daily_trend')}
                className="h-8 text-xs border-slate-200 dark:border-slate-700"
              >
                <Download size={12} className="mr-1" />
                <span>Export CSV</span>
              </Button>
            </CardHeader>
            <CardContent className="p-5">
              {dailyTrend.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs">
                  No trend data in the selected date range.
                </div>
              ) : (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyTrend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis 
                        dataKey="day" 
                        tick={{ fontSize: 11 }} 
                        stroke="#94a3b8" 
                      />
                      <YAxis 
                        yAxisId="left" 
                        tick={{ fontSize: 11 }} 
                        stroke="#94a3b8" 
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        tick={{ fontSize: 11 }} 
                        stroke="#10b981" 
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0f172a', 
                          border: '1px solid #334155', 
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: '#fff'
                        }} 
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="bookings_created" 
                        name="Bookings" 
                        stroke="#6366f1" 
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="jobs_completed" 
                        name="Completed Jobs" 
                        stroke="#38bdf8" 
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="invoice_total" 
                        name="Invoiced Revenue (₹)" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ======================================================== */}
          {/* 3. REVENUE BY SERVICE & PROFESSIONAL PERFORMANCE         */}
          {/* ======================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Service Table */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 flex flex-col justify-between">
              <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Receipt size={16} className="text-primary" />
                    <span>Revenue by Service Type</span>
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Highest generating catalog services
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(revenueByService, 'revenue_by_service')}
                  className="h-7 text-xs border-slate-200 dark:border-slate-700"
                >
                  <Download size={11} className="mr-1" />
                  <span>CSV</span>
                </Button>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-x-auto">
                {revenueByService.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-xs">
                    No completed service billings found in this period.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="p-3">Service Name</th>
                        <th className="p-3 text-center">Jobs</th>
                        <th className="p-3 text-right">Taxable</th>
                        <th className="p-3 text-right">Total Invoiced</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {revenueByService.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="p-3 font-semibold text-slate-900 dark:text-white">
                            {row.service_name}
                          </td>
                          <td className="p-3 text-center text-slate-600 dark:text-slate-300">
                            {row.bookings_count}
                          </td>
                          <td className="p-3 text-right font-mono text-slate-600 dark:text-slate-400">
                            ₹{Number(row.taxable_value || 0).toFixed(2)}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.total_amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Professional Commission Performance */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 flex flex-col justify-between">
              <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Users size={16} className="text-indigo-500" />
                    <span>Staff & Technician Performance</span>
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Job fulfillment, payouts, and customer ratings
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(performance, 'staff_performance')}
                  className="h-7 text-xs border-slate-200 dark:border-slate-700"
                >
                  <Download size={11} className="mr-1" />
                  <span>CSV</span>
                </Button>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-x-auto">
                {performance.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-xs">
                    No staff job completions found in this period.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="p-3">Staff Member</th>
                        <th className="p-3 text-center">Jobs (Done/Assigned)</th>
                        <th className="p-3 text-right">Pro Payout</th>
                        <th className="p-3 text-right">Business Share</th>
                        <th className="p-3 text-center">Rating</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {performance.map((p, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="p-3 font-semibold text-slate-900 dark:text-white">
                            {p.full_name}
                          </td>
                          <td className="p-3 text-center text-slate-600 dark:text-slate-300">
                            {p.jobs_completed}/{p.jobs_assigned} ({p.completion_rate}%)
                          </td>
                          <td className="p-3 text-right font-mono font-medium text-emerald-600 dark:text-emerald-400">
                            ₹{Number(p.professional_share || 0).toFixed(2)}
                          </td>
                          <td className="p-3 text-right font-mono font-medium text-primary">
                            ₹{Number(p.business_share || 0).toFixed(2)}
                          </td>
                          <td className="p-3 text-center">
                            {p.avg_rating ? (
                              <span className="inline-flex items-center gap-1 font-bold text-amber-500">
                                <Star size={11} className="fill-amber-400" />
                                {Number(p.avg_rating).toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ======================================================== */}
          {/* 4. MATERIALS CONSUMPTION & OUTSTANDING DUE                */}
          {/* ======================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Materials Consumption Table */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Package size={16} className="text-purple-500" />
                    <span>Materials & Parts Consumed</span>
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Spare parts and materials billed across ongoing and completed jobs
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(materials, 'materials_consumed')}
                  className="h-7 text-xs border-slate-200 dark:border-slate-700"
                >
                  <Download size={11} className="mr-1" />
                  <span>CSV</span>
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {materials.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-xs">
                    No extra materials or spare parts billed in this period.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="p-3">Material / Item</th>
                        <th className="p-3 text-center">Qty Used</th>
                        <th className="p-3 text-right">Total Billed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {materials.map((m, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="p-3 font-semibold text-slate-900 dark:text-white">
                            {m.item_name}
                          </td>
                          <td className="p-3 text-center text-slate-600 dark:text-slate-300">
                            {m.total_quantity} {m.unit_name || ''}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-slate-900 dark:text-white">
                            ₹{Number(m.total_amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Outstanding Receivables Table */}
            <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <IndianRupee size={16} className="text-amber-500" />
                    <span>Outstanding Due (Pending Receipts)</span>
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Completed bookings with unpaid balances
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(outstanding, 'services_outstanding')}
                  className="h-7 text-xs border-slate-200 dark:border-slate-700"
                >
                  <Download size={11} className="mr-1" />
                  <span>CSV</span>
                </Button>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {outstanding.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-xs">
                    ✓ All completed jobs are fully paid. No outstanding receivables!
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="p-3">Booking #</th>
                        <th className="p-3">Customer</th>
                        <th className="p-3 text-right">Invoice Amount</th>
                        <th className="p-3 text-right">Pending Due</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {outstanding.map((o, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="p-3 font-mono font-bold text-primary">
                            {o.booking_number}
                          </td>
                          <td className="p-3">
                            <p className="font-semibold text-slate-900 dark:text-white">{o.customer_name}</p>
                            <span className="text-[10px] text-slate-400">{o.customer_phone || ''}</span>
                          </td>
                          <td className="p-3 text-right font-mono text-slate-600 dark:text-slate-300">
                            ₹{Number(o.total_amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-rose-600 dark:text-rose-400">
                            ₹{Number(o.pending_amount || o.total_amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Task 4: Online collections held by platform Card (RPC: get_service_online_collections) */}
            {(() => {
              const totals = onlineCollectionsData?.totals || {};
              const rows = onlineCollectionsData?.rows || [];
              const pendingTransfer = Number(totals.pending_transfer || 0);
              const merchantShareTotal = Number(totals.merchant_share || 0);
              const grossTotal = Number(totals.gross || 0);
              const feeTotal = Number(totals.fee || 0);
              const commissionTotal = Number(totals.commission || 0);

              return (
                <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 col-span-1 md:col-span-2">
                  <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Wallet size={16} className="text-primary" />
                        <span>Online Collections & Platform Settlements</span>
                      </CardTitle>
                      <CardDescription className="text-xs text-slate-500 mt-0.5">
                        Payments collected online via Razorpay gateway for your service bookings
                      </CardDescription>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-left">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase block">
                          Total Gross ({totals.count || 0})
                        </span>
                        <span className="text-xs font-mono font-bold text-slate-900 dark:text-white">
                          ₹{grossTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-left">
                        <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 block uppercase">
                          Your Net Share
                        </span>
                        <span className="text-xs font-mono font-bold text-emerald-900 dark:text-emerald-200">
                          ₹{merchantShareTotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-left">
                        <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 block uppercase">
                          Pending Payout
                        </span>
                        <span className="text-xs font-mono font-bold text-amber-900 dark:text-amber-200">
                          ₹{pendingTransfer.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    {collectionsLoading ? (
                      <div className="py-8 flex items-center justify-center text-xs text-slate-400 gap-2">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Loading online collections...</span>
                      </div>
                    ) : rows.length === 0 ? (
                      <div className="py-8 text-center text-slate-400 text-xs">
                        No online platform collections recorded for this period.
                      </div>
                    ) : (
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                          <tr>
                            <th className="p-3">Date</th>
                            <th className="p-3">Booking / Payment ID</th>
                            <th className="p-3 text-right">Gross</th>
                            <th className="p-3 text-right">Gateway / Comm.</th>
                            <th className="p-3 text-right">Your Net Share</th>
                            <th className="p-3 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {rows.map((col: any, idx: number) => (
                            <tr key={col.payment_id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                              <td className="p-3 text-slate-500 whitespace-nowrap">
                                {col.at ? new Date(col.at).toLocaleDateString() : '—'} {col.at ? new Date(col.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                              </td>
                              <td className="p-3">
                                <span className="font-mono text-slate-800 dark:text-slate-200 block">
                                  {col.booking_number || (col.booking_id ? `Booking #${col.booking_id.slice(0, 8)}...` : col.payment_id)}
                                </span>
                                <span className="text-[10px] text-slate-400 capitalize">
                                  {col.purpose ? col.purpose.replace(/_/g, ' ') : 'Online Payment'}
                                  {col.payment_id && ` • ${col.payment_id}`}
                                </span>
                              </td>
                              <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-300">
                                ₹{Number(col.gross || 0).toFixed(2)}
                              </td>
                              <td className="p-3 text-right font-mono text-slate-500">
                                -₹{(Number(col.fee || 0) + Number(col.commission || 0)).toFixed(2)}
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                ₹{Number(col.merchant_share || 0).toFixed(2)}
                              </td>
                              <td className="p-3 text-center">
                                {col.settlement_status === 'settled' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                    <CheckCircle2 size={10} />
                                    Settled
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                    Pending Payout
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                      <span>Online collections are tracked by platform account and transferred to your registered bank account.</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={fetchOnlineCollections}
                        className="text-xs h-7 gap-1 text-slate-500"
                      >
                        <RefreshCw size={11} className={collectionsLoading ? 'animate-spin' : ''} />
                        <span>Refresh</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

export default ServicesReportsView;
